import { useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { TranslatedInput, TranslatedTextarea } from '@/components/TranslatedField'
import {
  saveEstateType,
  saveGroup,
  saveHint,
  saveSectionType,
} from '@/features/rules/rules-api'
import { describeWriteError } from '@/features/users/permissions'
import {
  buildFactKeyPalette,
  groupScopedQuestions,
  insertAtCursor,
  type FactKeyPalette,
} from '@/features/rules/fact-key-palette'
import { KeyPalette } from '@/features/rules/LogicPalette'
import { TRANSLATION_LANGUAGES } from '@/lib/constants'
import type {
  ConfigQuestion,
  EstateType,
  QuestionGroup,
  QuestionHint,
  QuestionOption,
  SectionType,
} from '@/lib/types'
import { sanitizeTranslations as sanitize } from '@/lib/utils'

/** Shared shell: title, body, error box and the save/cancel footer. */
function EditorDialog({
  title,
  description,
  canSave,
  isSaving,
  error,
  onSave,
  onClose,
  children,
  className,
}: {
  title: string
  description: string
  canSave: boolean
  isSaving: boolean
  error: string | null
  onSave: () => void
  onClose: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={className ?? 'max-w-lg'}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {children}
          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={onSave} disabled={!canSave || isSaving}>
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Name + per-language overrides, used by every node of the tree. */
function NameFields({
  label,
  name,
  onName,
  i18n,
  onI18n,
}: {
  label: string
  name: string
  onName: (value: string) => void
  i18n: Record<string, string>
  onI18n: Dispatch<SetStateAction<Record<string, string>>>
}) {
  return (
    <>
      <label className="grid gap-1.5 text-sm">
        <span className="font-medium">{label}</span>
        <Input value={name} onChange={(event) => onName(event.target.value)} autoFocus />
      </label>
      <div className="grid gap-2 rounded-lg border p-3">
        <span className="text-sm font-medium">Переводы</span>
        {TRANSLATION_LANGUAGES.map((language) => (
          <TranslatedInput
            key={language.tag}
            value={i18n[language.tag] ?? ''}
            onChange={(event) => onI18n({ ...i18n, [language.tag]: event.target.value })}
            source={name}
            lang={language.tag}
            onTranslated={(text) => onI18n((prev) => ({ ...prev, [language.tag]: text }))}
            placeholder={language.label}
            aria-label={`${label} (${language.label})`}
          />
        ))}
      </div>
    </>
  )
}

function OrderField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium">Порядок</span>
      <Input type="number" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  )
}

function useEditorState(initialName: string, initialOrder: number, initialI18n: Record<string, string>) {
  const [name, setName] = useState(initialName)
  const [order, setOrder] = useState(String(initialOrder))
  const [i18n, setI18n] = useState<Record<string, string>>(initialI18n)
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return { name, setName, order, setOrder, i18n, setI18n, isSaving, setSaving, error, setError }
}

export function EstateTypeDialog({
  estateType,
  nextOrder,
  onClose,
}: {
  estateType: EstateType | null
  nextOrder: number
  onClose: () => void
}) {
  const state = useEditorState(
    estateType?.name ?? '',
    estateType?.orderIndex ?? nextOrder,
    estateType?.nameI18n ?? {},
  )

  const handleSave = async () => {
    state.setSaving(true)
    state.setError(null)
    try {
      await saveEstateType(estateType?.id ?? null, {
        name: state.name.trim(),
        nameI18n: sanitize(state.i18n),
        orderIndex: Number.parseInt(state.order, 10) || 0,
      })
      onClose()
    } catch (caught) {
      state.setError(describeWriteError(caught))
    } finally {
      state.setSaving(false)
    }
  }

  return (
    <EditorDialog
      title={estateType ? 'Тип объекта' : 'Новый тип объекта'}
      description="Верхний уровень конфигурации: квартира, дом, офис…"
      canSave={state.name.trim().length > 0}
      isSaving={state.isSaving}
      error={state.error}
      onSave={() => void handleSave()}
      onClose={onClose}
    >
      <NameFields
        label="Название (ru)"
        name={state.name}
        onName={state.setName}
        i18n={state.i18n}
        onI18n={state.setI18n}
      />
      <OrderField value={state.order} onChange={state.setOrder} />
    </EditorDialog>
  )
}

export function SectionTypeDialog({
  sectionType,
  estateTypeId,
  groups,
  nextOrder,
  onClose,
}: {
  sectionType: SectionType | null
  estateTypeId: string
  groups: QuestionGroup[]
  nextOrder: number
  onClose: () => void
}) {
  const state = useEditorState(
    sectionType?.name ?? '',
    sectionType?.orderIndex ?? nextOrder,
    sectionType?.nameI18n ?? {},
  )
  const [groupIds, setGroupIds] = useState<string[]>(sectionType?.groupIds ?? [])
  // Global groups apply everywhere, so they are never part of this list.
  const selectable = groups.filter((group) => !group.isGlobal)
  // Linked ids whose group was deleted: invisible in the checkbox list, so shown separately (in red)
  // with a way to unlink them. Saving writes `groupIds` as edited here.
  const missingGroupIds = groupIds.filter((id) => !groups.some((group) => group.id === id))

  const handleSave = async () => {
    state.setSaving(true)
    state.setError(null)
    try {
      await saveSectionType(sectionType?.id ?? null, {
        estateTypeId,
        name: state.name.trim(),
        nameI18n: sanitize(state.i18n),
        orderIndex: Number.parseInt(state.order, 10) || 0,
        groupIds,
      })
      onClose()
    } catch (caught) {
      state.setError(describeWriteError(caught))
    } finally {
      state.setSaving(false)
    }
  }

  return (
    <EditorDialog
      title={sectionType ? 'Тип раздела' : 'Новый тип раздела'}
      description="Раздел внутри типа объекта: комната, санузел, балкон… Вопросы задаются отдельно."
      canSave={state.name.trim().length > 0}
      isSaving={state.isSaving}
      error={state.error}
      onSave={() => void handleSave()}
      onClose={onClose}
    >
      <NameFields
        label="Название (ru)"
        name={state.name}
        onName={state.setName}
        i18n={state.i18n}
        onI18n={state.setI18n}
      />
      <OrderField value={state.order} onChange={state.setOrder} />

      <div className="grid gap-2 rounded-lg border p-3">
        <span className="text-sm font-medium">Подключённые группы вопросов</span>
        {selectable.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            Не глобальных групп пока нет. Глобальные группы применяются ко всем разделам
            автоматически.
          </p>
        ) : (
          selectable.map((group) => (
            <label key={group.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-current"
                checked={groupIds.includes(group.id)}
                onChange={(event) =>
                  setGroupIds(
                    event.target.checked
                      ? [...groupIds, group.id]
                      : groupIds.filter((id) => id !== group.id),
                  )
                }
              />
              {group.name}
            </label>
          ))
        )}
        {missingGroupIds.length > 0 ? (
          <div className="border-destructive/50 bg-destructive/10 grid gap-2 rounded-md border p-2">
            <span className="text-destructive text-xs font-medium">
              Подключены удалённые группы — на расчёт не влияют, их лучше отвязать и сохранить:
            </span>
            {missingGroupIds.map((id) => (
              <div key={id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-destructive min-w-0 truncate">
                  Группа удалена · <code className="font-mono text-xs">{id}</code>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setGroupIds(groupIds.filter((item) => item !== id))}
                >
                  <X />
                  Отвязать
                </Button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </EditorDialog>
  )
}

export function GroupDialog({
  group,
  nextOrder,
  onClose,
}: {
  group: QuestionGroup | null
  nextOrder: number
  onClose: () => void
}) {
  const state = useEditorState(group?.name ?? '', group?.orderIndex ?? nextOrder, group?.nameI18n ?? {})
  const [isGlobal, setGlobal] = useState(group?.isGlobal ?? false)

  const handleSave = async () => {
    state.setSaving(true)
    state.setError(null)
    try {
      await saveGroup(group?.id ?? null, {
        name: state.name.trim(),
        nameI18n: sanitize(state.i18n),
        orderIndex: Number.parseInt(state.order, 10) || 0,
        isGlobal,
      })
      onClose()
    } catch (caught) {
      state.setError(describeWriteError(caught))
    } finally {
      state.setSaving(false)
    }
  }

  return (
    <EditorDialog
      title={group ? 'Группа вопросов' : 'Новая группа вопросов'}
      description="Переиспользуемый набор вопросов. Глобальная группа добавляется во все помещения."
      canSave={state.name.trim().length > 0}
      isSaving={state.isSaving}
      error={state.error}
      onSave={() => void handleSave()}
      onClose={onClose}
    >
      <NameFields
        label="Название (ru)"
        name={state.name}
        onName={state.setName}
        i18n={state.i18n}
        onI18n={state.setI18n}
      />
      <OrderField value={state.order} onChange={state.setOrder} />

      <label className="grid gap-1.5 text-sm">
        <span className="font-medium">Область применения</span>
        <Select
          value={isGlobal ? 'global' : 'optional'}
          onChange={(event) => setGlobal(event.target.value === 'global')}
        >
          <option value="global">Глобальная — во всех помещениях</option>
          <option value="optional">По подключению — только где выбрана</option>
        </Select>
      </label>
    </EditorDialog>
  )
}

export function HintDialog({
  hint,
  groupId,
  nextOrder,
  allQuestions,
  allOptions,
  groups,
  onClose,
}: {
  hint: QuestionHint | null
  groupId: string
  nextOrder: number
  /** The whole rules config, for the fact-key palette of the condition. */
  allQuestions: ConfigQuestion[]
  allOptions: QuestionOption[]
  groups: QuestionGroup[]
  onClose: () => void
}) {
  const [text, setText] = useState(hint?.text ?? '')
  const [condition, setCondition] = useState(hint?.condition ?? '')
  const [textI18n, setTextI18n] = useState<Record<string, string>>(hint?.textI18n ?? {})
  const [order, setOrder] = useState(String(hint?.orderIndex ?? nextOrder))
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const conditionRef = useRef<HTMLInputElement>(null)

  /**
   * Keys a hint condition may use — exactly the app's `HintsViewModel`: every GROUP question (a
   * group is reused across rooms, so it cannot depend on one section's own questions), with
   * KEY_SET / REPEATER / QUESTION_LIST expanded into their item / field keys.
   */
  const palette: FactKeyPalette = useMemo(() => {
    const optionsByQuestion = new Map<string, QuestionOption[]>()
    for (const option of allOptions) {
      const list = optionsByQuestion.get(option.questionId)
      if (list) list.push(option)
      else optionsByQuestion.set(option.questionId, [option])
    }
    for (const list of optionsByQuestion.values()) {
      list.sort((left, right) => left.orderIndex - right.orderIndex)
    }
    return buildFactKeyPalette(groupScopedQuestions(allQuestions), groups, optionsByQuestion)
  }, [allQuestions, allOptions, groups])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveHint(hint?.id ?? null, {
        groupId,
        text: text.trim(),
        textI18n: sanitize(textI18n),
        condition: condition.trim(),
        orderIndex: Number.parseInt(order, 10) || 0,
      })
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <EditorDialog
      title={hint ? 'Подсказка' : 'Новая подсказка'}
      description="Всплывает в интервью, когда условие становится истинным. На расчёт не влияет."
      className="max-h-[92vh] max-w-2xl overflow-y-auto"
      canSave={text.trim().length > 0}
      isSaving={isSaving}
      error={error}
      onSave={() => void handleSave()}
      onClose={onClose}
    >
      <label className="grid gap-1.5 text-sm">
        <span className="font-medium">Текст (ru)</span>
        <Textarea value={text} onChange={(event) => setText(event.target.value)} rows={3} autoFocus />
      </label>

      <div className="grid gap-2 rounded-lg border p-3">
        <span className="text-sm font-medium">Переводы текста</span>
        {TRANSLATION_LANGUAGES.map((language) => (
          <TranslatedTextarea
            key={language.tag}
            value={textI18n[language.tag] ?? ''}
            onChange={(event) => setTextI18n({ ...textI18n, [language.tag]: event.target.value })}
            source={text}
            lang={language.tag}
            onTranslated={(translated) =>
              setTextI18n((prev) => ({ ...prev, [language.tag]: translated }))
            }
            placeholder={language.label}
            rows={2}
            aria-label={`Текст (${language.label})`}
          />
        ))}
      </div>

      <div className="grid gap-2 text-sm">
        <label htmlFor="hint-condition" className="font-medium">
          Условие
        </label>
        <KeyPalette
          palette={palette}
          onInsert={(token) =>
            insertAtCursor(conditionRef.current, condition, token, setCondition)
          }
        />
        <Input
          id="hint-condition"
          ref={conditionRef}
          value={condition}
          onChange={(event) => setCondition(event.target.value)}
          placeholder="{height} > 0 && {area} > 0 && {area} <= 25"
          className="font-mono"
        />
        <span className="text-muted-foreground text-xs">
          Пустое условие — подсказка не показывается никогда (иначе она всплывала бы при входе в
          помещение). Ошибка в выражении тоже означает «не показывать».
        </span>
      </div>

      <OrderField value={order} onChange={setOrder} />
    </EditorDialog>
  )
}
